import axios from 'axios'
import { set } from 'lodash'
import moment from 'moment'
import {
    createCardToken,
    createPayment,
    getPayer,
    deletePaymentSource,
} from '@shared/Pinch'

const INPUT_CHANGED = 'INPUT_CHANGED'
const RADIO_CHANGED = 'RADIO_CHANGED'
const CLEAR_FORM = 'CLEAR_FORM'
const SET_SETTINGS = 'SET_SETTINGS'
const SET_PAYMENT_METHODS = 'SET_PAYMENT_METHODS'
const SET_PAYMENT_CARDS = 'SET_PAYMENT_CARDS'
const SET_PAYMENT_CARDS_FETCHED = 'SET_PAYMENT_CARDS_FETCHED'
const SET_TIMESTAMP = 'SET_TIMESTAMP'

const paymentForm = {
    builder: {
        address: null,
        city: null,
        state: null,
        postcode: null,
        country: null,
    },
    payment_method: null,
    note_accepted: false,
}

/**
 * Vuex state for job module
 * @type {Object}
 */
const state = {
    paymentForm: { ...paymentForm },
    paymentMethods: [],
    paycreteSettings: [],
    paymentCards: [],
    paymentCardsFetched: false,
    contingencyPaymentPercentage: 15,
}

/**
 * Vuex mutations for job module
 * @type {Object}
 */
const mutations = {
    [SET_SETTINGS]: (state, payload) => {
        state.paycreteSettings = payload
    },

    [SET_PAYMENT_METHODS]: (state, payload) => {
        state.paymentMethods = payload
    },

    [SET_PAYMENT_CARDS]: (state, payload) => {
        state.paymentCards = payload
    },

    [SET_PAYMENT_CARDS_FETCHED]: (state, payload) => {
        state.paymentCardsFetched = payload
    },

    [INPUT_CHANGED]: (state, payload) => {
        const { key, value } = payload
        const stack = Object.assign({}, state.paymentForm)

        set(stack, key, value)
        state.paymentForm = stack
    },

    [RADIO_CHANGED]: (state, { value }) => {
        state.paymentForm.payment_method = value
    },

    [CLEAR_FORM]: (state) => {
        state.paymentForm = { ...paymentForm }
        state.paymentForm.builder = { ...paymentForm.builder }
    },

    [SET_TIMESTAMP]: (state, payload) => {
        state.timestamp = payload
    },
}

/**
 * Vuex actions for job module
 * @type {Object}
 */
const actions = {
    async fetchSettings({ commit }) {
        const { data } = await axios.post('/api/settings', {
            alias: 'paycrete',
        })
        commit(SET_SETTINGS, data.settings)
        commit(SET_TIMESTAMP, data.timestamp)
    },

    async fetchPaymentMethods({ commit }) {
        const { data } = await axios.get(
            '/api/jobs/pinch-payments/retrieve-methods',
        )
        commit(SET_PAYMENT_METHODS, data)
    },

    async fetchBuilderCards({ commit }, { id }) {
        try {
            const { data } = await getPayer({
                payerId: id,
            })

            commit(SET_PAYMENT_CARDS, data.data.sources)
            commit(SET_PAYMENT_CARDS_FETCHED, true)
            return { data }
        } catch (e) {
            if (e.response.status === 404) {
                this.$router.push({ name: 'jobs' })
            }
        }
    },

    async removeBuilderCard({ commit }, { source, id }) {
        try {
            const { data } = await deletePaymentSource({
                payerId: id,
                sourceId: source,
            })

            return data
        } catch (e) {
            if (e.response.status === 404) {
                this.$router.push({ name: 'jobs' })
            }
        }
    },

    async getCreditCardToken({}, payload) {
        const card = {
            cardNumber: payload.cardNumber,
            cardHolderName: payload.cardHolderName,
            expiryYear: moment(payload.expiryDate, 'MM / YY').format('YYYY'),
            expiryMonth: moment(payload.expiryDate, 'MM / YY').format('MM'),
            cvc: payload.cvc,
        }

        try {
            const response = await createCardToken(card)

            return response
        } catch (error) {
            console.log({ error })
            const errors = Object.values(error.response.data.errors).flat()
            return { errors }
        }
    },

    async submitPayment({ dispatch }, payload) {
        const {
            job,
            pinch_payer_id,
            pinch_source_id,
            credit_card_token,
            onFailed,
            onSuccess,
            bid_amount,
        } = payload

        const final_amount = bid_amount || job.total_amount_with_gst

        try {
            if (!pinch_payer_id || !pinch_source_id) {
                if (!credit_card_token)
                    throw new Error('Missing Payment source')
            }

            const description = `CC Payment for ${job.builder.name}'s job located at ${job.address.long_address} amounting to ${final_amount} AUD`

            const { data } = await createPayment({
                ...(credit_card_token
                    ? {
                          creditCardToken: credit_card_token,
                          fullName: job.builder.name,
                      }
                    : {
                          payerId: pinch_payer_id,
                          sourceId: pinch_source_id,
                      }),
                email: job.builder.email,
                amount: parseInt(parseFloat(final_amount) * 100),
                description,
            })

            dispatch('storePayment', {
                job: job,
                pinch_payment_id: data.data.id,
                pinch_source_id,
                computed_amount: bid_amount || job.total_amount_with_gst,
                paid_at: true,
            })

            if (onSuccess instanceof Function) await onSuccess()
        } catch (e) {
            if (onFailed instanceof Function) await onFailed(e)
        }
    },

    async savePaymentDetailsToPinch({ dispatch }, payload) {
        const {
            card,
            app,
            job,
            pinch_payment_id,
            pinch_source_id,
            bid_amount,
            onFailed,
            onSuccess,
        } = payload

        if (app && card) {
            try {
                dispatch('storePayment', {
                    job: job,
                    pinch_payment_id,
                    pinch_source_id,
                    computed_amount: bid_amount || job.total_amount_with_gst,
                    paid_at: true,
                })

                if (onSuccess instanceof Function) await onSuccess()
            } catch (e) {
                if (onFailed instanceof Function)
                    await onFailed(e.response.data)
            }
        } else {
            if (onSuccess instanceof Function) await onSuccess()
        }
    },

    async storePayment({ state }, payload) {
        const { paymentForm } = state
        const {
            job,
            paid_at,
            computed_amount,
            cycle_amount,
            bid_amount,
            pinch_payment_id,
            pinch_source_id,
        } = payload

        let used_total_amount = computed_amount || job.total_amount_with_gst
        let used_bid_amount = bid_amount || 0
        let used_cycle_amount = cycle_amount || 0

        try {
            await axios.post(`/api/jobs/${job.uuid}/pinch-payments/store`, {
                status: 'approved',
                payment_method: paymentForm.payment_method,
                bid_amount: parseFloat(used_bid_amount),
                total_amount: used_total_amount.toString(),
                cycle_amount: used_cycle_amount.toString(),
                pinch_payment_id,
                pinch_source_id,
                paid_at: paid_at,
            })
        } catch (e) {
            console.log({ error: e })
        }
    },

    clearPaymentForm({ commit }) {
        commit(CLEAR_FORM)
    },

    inputChanged({ commit }, payload) {
        const { key, value } = payload
        commit(INPUT_CHANGED, { key, value })
    },

    radioChanged({ commit }, { key, value }) {
        commit(RADIO_CHANGED, { key, value })
    },
}

/**
 * Vuex getters for job module
 * @type {Object}
 */
const getters = {
    paymentForm: (state) => state.paymentForm,
    paycreteSettings: (state) => state.paycreteSettings,
    paymentMethods: (state) => state.paymentMethods,
    paymentCards: (state) => state.paymentCards,
    paymentCardsFetched: (state) => state.paymentCardsFetched,
    ccCardsMethod: (state) => state.paymentMethods.ccCards,
    accountFormMethod: (state) => state.paymentMethods.accountForm,
    othersMethod: (state) => state.paymentMethods.others,
    directContactMethod: (state) => state.paymentMethods.directContact,
    directDepositMethod: (state) => state.paymentMethods.directDeposit,
    paycreteMethod: (state) => state.paymentMethods.paycrete,
    gst: (state) =>
        parseFloat(
            state.paycreteSettings.find(
                (setting) => setting.alias === 'paycrete_gst',
            )?.value || 0,
        ),
    contingencyPaymentPercentage: (state) =>
        parseFloat(
            state.paycreteSettings.find(
                (setting) => setting.alias === 'paycrete_contingency',
            )?.value || state.contingencyPaymentPercentage,
        ),
    bsb: (state) =>
        state.paycreteSettings.find(
            (setting) => setting.alias === 'paycrete_bsb',
        ).value,
    account_number: (state) =>
        state.paycreteSettings.find(
            (setting) => setting.alias === 'paycrete_account_number',
        ).value,
    delivery_fee: (state) =>
        parseFloat(
            state.paycreteSettings.find(
                (setting) => setting.alias === 'paycrete_delivery_fee',
            )?.value || 0,
        ),
    delivery_fee_disclaimer: (state) =>
        state.paycreteSettings.find(
            (setting) => setting.alias === 'paycrete_delivery_fee_disclaimer',
        ).value,
    fee_markup: (state) =>
        parseFloat(
            state.paycreteSettings.find(
                (setting) => setting.alias === 'paycrete_fee_markup',
            ).value,
        ),
    payment_cycles: (state) =>
        parseInt(
            state.paycreteSettings.find(
                (setting) => setting.alias === 'paycrete_payment_cycles',
            ).value,
        ),
    payment_duration: (state) =>
        parseInt(
            state.paycreteSettings.find(
                (setting) => setting.alias === 'paycrete_payment_duration',
            ).value,
        ),
    paycrete_disclaimer: (state) =>
        state.paycreteSettings.find(
            (setting) => setting.alias === 'paycrete_disclaimer',
        ).value,
    paycrete_enabled: (state) =>
        state.paycreteSettings.find(
            (setting) => setting.alias === 'paycrete_enabled',
        ).is_enabled,
    paycrete_credit_limit: (state) =>
        state.paycreteSettings.find(
            (setting) => setting.alias === 'paycrete_credit_limit',
        ).value,
    paycrete_terms_and_conditions: (state) =>
        state.paycreteSettings.find(
            (setting) => setting.alias === 'paycrete_terms_and_conditions',
        ).value,
    paycrete_privacy_policy: (state) =>
        state.paycreteSettings.find(
            (setting) => setting.alias === 'paycrete_privacy_policy',
        ).value,
    settings_timestamp: (state) => state.timestamp,
}

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
}
